import React, {useEffect, useState} from "react";
import {Box, Stack, Typography, IconButton, Button, Modal, Fade, Skeleton, CircularProgress} from "@mui/material";
import {HugeiconsIcon} from "@hugeicons/react";
import {
  AlertCircleIcon, Calendar03Icon, Cancel01Icon, CheckmarkCircle01Icon, Clock01Icon, Copy01Icon, Download04Icon,
  File02Icon, Layers01Icon, PencilEdit01Icon, SdCardIcon, UserIcon,
} from "@hugeicons/core-free-icons";
import {toast} from "react-toastify";
import {KnowledgeManagerRepository} from "../../../../repositories/KnowledgeManagerRepository";
import {SplitMarkdownEditor} from "../../../../components/markdown/SplitMarkdownEditor";

const TYPE_LABELS = {
  'application/pdf': 'PDF',
  'text/markdown': 'Markdown',
  'text/plain': 'Text',
  'text/csv': 'CSV',
  'application/json': 'JSON',
  'text/html': 'HTML',
  'application/msword': 'Word',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel',
};

const docTypeLabel = (doc) => {
  if (doc.type && TYPE_LABELS[doc.type]) return TYPE_LABELS[doc.type];
  const ext = doc.name?.includes('.') ? doc.name.split('.').pop().toUpperCase() : null;
  return ext || 'File';
};

// The backend only exposes extracted text, not the original binary — so for formats that aren't
// natively text (PDF/Word/Excel) the download falls back to .txt instead of lying about the extension.
const DOWNLOAD_FORMATS = {
  'text/markdown': {ext: 'md', mime: 'text/markdown;charset=utf-8'},
  'text/plain': {ext: 'txt', mime: 'text/plain;charset=utf-8'},
  'text/csv': {ext: 'csv', mime: 'text/csv;charset=utf-8'},
  'application/json': {ext: 'json', mime: 'application/json;charset=utf-8'},
  'text/html': {ext: 'html', mime: 'text/html;charset=utf-8'},
};
const FALLBACK_DOWNLOAD_FORMAT = {ext: 'txt', mime: 'text/plain;charset=utf-8'};

const downloadFormat = (doc) => DOWNLOAD_FORMATS[doc.type] || FALLBACK_DOWNLOAD_FORMAT;

const downloadFileName = (doc) => {
  const {ext} = downloadFormat(doc);
  const base = doc.name?.includes('.') ? doc.name.slice(0, doc.name.lastIndexOf('.')) : (doc.name || `document-${doc.id}`);
  return `${base}.${ext}`;
};

const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = window.document.createElement("a");
  link.href = url;
  link.download = fileName;
  window.document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const formatSize = (bytes) => {
  if (!bytes) return "0 B";
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / (1024 * 1024)).toFixed(1) + " MB";
};

const formatDate = (iso) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", {month: "short", day: "numeric", year: "numeric"});
};

const STATUS_INFO = {
  indexed: {color: '#2F9A6A', icon: CheckmarkCircle01Icon, label: 'Indexed', badge: 'Ready to query'},
  processing: {color: '#D9A13A', icon: Clock01Icon, label: 'Processing', badge: 'Processing'},
  uploading: {color: '#D9A13A', icon: Clock01Icon, label: 'Uploading', badge: 'Processing'},
  failed: {color: '#DC2626', icon: AlertCircleIcon, label: 'Failed', badge: 'Indexing failed'},
};

const statusInfo = (status) => STATUS_INFO[status] || {color: '#8A8AA0', icon: Clock01Icon, label: status || 'Pending', badge: 'Pending'};

const MetaBox = ({icon, label, children, span}) => (
  <Box sx={{gridColumn: span ? `span ${span}` : undefined, bgcolor: "#FAFAFD", borderRadius: "12px", p: 1.5, minWidth: 0}}>
    <Stack direction="row" alignItems="center" gap={0.5}>
      {icon}
      <Typography sx={{fontSize: 10, fontWeight: 600, letterSpacing: "0.04em", color: "#8A8AA0", textTransform: "uppercase"}}>{label}</Typography>
    </Stack>
    <Box sx={{mt: 0.75}}>{children}</Box>
  </Box>
);

const squareIconBtnSx = {width: 34, height: 34, borderRadius: '10px', border: '1px solid #ECECF3', color: '#8A8AA0', flexShrink: 0};

export const DocumentPreviewModalNew = ({document, open, onClose, onUpdated}) => {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !document?.id) {
      setContent(null);
      setError(null);
      setEditing(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    KnowledgeManagerRepository.getDocumentContent(document.id)
      .then(data => {if (!cancelled) setContent(data);})
      .catch(() => {if (!cancelled) setError("Content isn't available for this document yet.");})
      .finally(() => {if (!cancelled) setLoading(false);});
    return () => {cancelled = true;};
  }, [open, document?.id]);

  const handleStartEdit = () => {
    setEditedContent(content?.content || "");
    setEditing(true);
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditedContent("");
  };

  const handleCopy = () => {
    if (!content?.content) return;
    navigator.clipboard.writeText(content.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    if (!content?.content || !document) return;
    const {mime} = downloadFormat(document);
    downloadBlob(new Blob([content.content], {type: mime}), downloadFileName(document));
  };

  const handleSave = async () => {
    if (!document?.id) return;
    setSaving(true);
    try {
      const updated = await KnowledgeManagerRepository.updateDocumentText(document.id, {
        content: editedContent, fileName: document.name,
      });
      setContent(prev => ({...prev, content: editedContent, characterCount: editedContent.length}));
      setEditing(false);
      toast.success("Document updated");
      onUpdated?.(updated);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  if (!document) return null;
  const status = statusInfo(document.status);

  return (
    <Modal open={open} onClose={onClose}>
      <Fade in={open}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: {xs: "calc(100% - 24px)", md: "min(900px, 94vw)"},
          maxHeight: "88vh", display: "flex", flexDirection: "column",
          bgcolor: '#fff', borderRadius: '20px', boxShadow: '0 24px 70px rgba(35, 38, 77, 0.22)', p: {xs: 2.5, md: 3}, outline: 'none',
        }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{mb: 2.5}}>
            <Stack direction="row" alignItems="center" gap={1.25} sx={{minWidth: 0}}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "12px", bgcolor: "rgba(132, 145, 247, 0.12)", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <HugeiconsIcon icon={File02Icon} size={18} color={"#8491F7"}/>
              </Box>
              <Box sx={{minWidth: 0}}>
                <Typography sx={{fontSize: 16, fontWeight: 700, color: '#23264D'}} noWrap>{document.name}</Typography>
                <Typography sx={{fontSize: 12, color: '#8A8AA0', mt: 0.25}}>{docTypeLabel(document)} document</Typography>
              </Box>
            </Stack>
            <IconButton onClick={onClose} sx={{...squareIconBtnSx, flexShrink: 0}}>
              <HugeiconsIcon icon={Cancel01Icon} size={18}/>
            </IconButton>
          </Stack>

          <Box sx={{display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 1.5, mb: 2.5}}>
            <MetaBox icon={<HugeiconsIcon icon={SdCardIcon} size={12}/>} label={"File size"}>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{formatSize(document.size)}</Typography>
            </MetaBox>
            <MetaBox icon={<HugeiconsIcon icon={Layers01Icon} size={12}/>} label={"Chunks"}>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{document.chunks ?? "N/A"}</Typography>
            </MetaBox>
            <MetaBox icon={<HugeiconsIcon icon={Calendar03Icon} size={12}/>} label={"Uploaded"}>
              <Typography sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{formatDate(document.uploadedAt)}</Typography>
            </MetaBox>
            <MetaBox icon={<HugeiconsIcon icon={status.icon} size={12}/>} label={"Status"} span={document.uploadedBy ? 1 : 3}>
              <Stack direction="row" alignItems="center" gap={0.5}>
                <HugeiconsIcon icon={status.icon} size={14} color={status.color}/>
                <Typography sx={{fontSize: 13, fontWeight: 700, color: status.color}}>{status.label}</Typography>
              </Stack>
            </MetaBox>
            {document.uploadedBy && (
              <MetaBox icon={<HugeiconsIcon icon={UserIcon} size={12}/>} label={"Uploaded by"} span={2}>
                <Typography noWrap sx={{fontSize: 13, fontWeight: 600, color: "#23264D"}}>{document.uploadedBy}</Typography>
              </MetaBox>
            )}
          </Box>

          <Box sx={{
            flex: 1, display: "flex", flexDirection: "column", overflow: "hidden",
            bgcolor: "#fff", border: "1px solid #ECECF3", borderRadius: "14px",
          }}>
            {!editing && (
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5} sx={{p: 2, pb: 1.5}}>
                <Box sx={{minWidth: 0}}>
                  <Typography sx={{fontSize: 10, fontWeight: 700, letterSpacing: "0.08em", color: "#23264D", textTransform: "uppercase"}}>
                    Document preview
                  </Typography>
                  <Typography sx={{fontSize: 12, color: "#8A8AA0", mt: 0.25}}>Extracted text used by the knowledge base</Typography>
                </Box>
                <Stack direction="row" alignItems="center" gap={0.75} sx={{flexShrink: 0}}>
                  <Stack direction="row" alignItems="center" gap={0.5} sx={{
                    px: 1.25, py: 0.4, borderRadius: "999px", bgcolor: `${status.color}1F`, color: status.color,
                  }}>
                    <HugeiconsIcon icon={status.icon} size={12}/>
                    <Typography sx={{fontSize: 11, fontWeight: 700, whiteSpace: "nowrap"}}>{status.badge}</Typography>
                  </Stack>
                  {!loading && !error && !!content?.content && (
                    <IconButton size="small" onClick={handleCopy} sx={{color: copied ? '#2F9A6A' : '#8A8AA0'}}>
                      <HugeiconsIcon icon={copied ? CheckmarkCircle01Icon : Copy01Icon} size={16}/>
                    </IconButton>
                  )}
                  {!loading && !error && !!content?.content && (
                    <IconButton size="small" onClick={handleDownload} sx={{color: '#8A8AA0'}}>
                      <HugeiconsIcon icon={Download04Icon} size={16}/>
                    </IconButton>
                  )}
                  {!loading && !error && (
                    <IconButton size="small" onClick={handleStartEdit} sx={{color: '#8A8AA0'}}>
                      <HugeiconsIcon icon={PencilEdit01Icon} size={16}/>
                    </IconButton>
                  )}
                </Stack>
              </Stack>
            )}
            <Box sx={{flex: 1, overflow: "auto", px: editing ? 0 : 2, pb: editing ? 0 : 2}}>
              {loading ? (
                <Stack gap={1} sx={{p: 2}}>
                  {Array.from({length: 6}).map((_, i) => <Skeleton key={i} height={14}/>)}
                </Stack>
              ) : error ? (
                <Typography sx={{fontSize: 13, color: "#8A8AA0", textAlign: "center", py: 3}}>{error}</Typography>
              ) : editing ? (
                <SplitMarkdownEditor value={editedContent} onChange={setEditedContent} bordered={false}/>
              ) : (
                <Typography component="pre" sx={{
                  fontSize: 13, color: "#23264D", lineHeight: 1.8, whiteSpace: "pre-wrap", wordBreak: "break-word",
                  fontFamily: "inherit", m: 0,
                }}>
                  {content?.content || "No content to display."}
                </Typography>
              )}
            </Box>
          </Box>

          {editing ? (
            <Stack direction="row" gap={1.5} justifyContent="flex-end" sx={{mt: 1.5}}>
              <Button
                onClick={handleCancelEdit}
                disabled={saving}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                  color: '#374151', bgcolor: '#FAFAFD', '&:hover': {bgcolor: '#ECECF3'},
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving}
                startIcon={saving ? <CircularProgress size={14} sx={{color: "#fff"}}/> : null}
                sx={{
                  textTransform: 'none', fontWeight: 600, fontSize: 13, borderRadius: '10px',
                  bgcolor: '#374151', color: '#fff', '&:hover': {bgcolor: '#1F2937'},
                  '&.Mui-disabled': {bgcolor: '#D1D5DB', color: '#9CA3AF'},
                }}
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            </Stack>
          ) : (content?.characterCount != null || document.chunks != null) && (
            <Typography sx={{fontSize: 11, color: "#8A8AA0", mt: 1.25}}>
              {content?.characterCount != null && `${content.characterCount.toLocaleString()} characters`}
              {content?.characterCount != null && document.chunks != null && " · "}
              {document.chunks != null && `${document.chunks} searchable chunks`}
            </Typography>
          )}
        </Box>
      </Fade>
    </Modal>
  );
};

export default DocumentPreviewModalNew;
